import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Task } from '../Interfaces';
import { Invoice } from '../../../types/accounting';
import { FeedEmailData } from '../../mail/components/EmailFeedContent';
import type { EmailDraft } from '../../../services/draftService';
import { getCachedGraphEmailBody, getOrFetchGraphEmailBody } from '../../mail/utils/graphEmailBodyCache';
import { resolveInlineGraphImages, stripCidImages } from '../../mail/utils/inlineImageResolver';
import { markEmailRead, isEmailRead } from '../../mail/utils/markEmailRead';
import { relTime } from '../../../themes/terminal/relTime';
import TaskDetailView from './TaskDetailView';
import TerminalComposer from './TerminalComposer';
import type { EmailOption } from './TerminalChipInput';
import { CustomerInfo } from './types';
import { RailSelection, ComposeMode, groupEmailsByThread } from './TerminalWorkspaceRail';

// ── Inline reply state (one at a time per thread pane) ───────────────────
// The thread pane maintains at most ONE inline composer. Clicking reply on
// a different email replaces it (the previous composer's draft auto-saves
// before unmount via SendEmailForm's cleanup effect, so nothing is lost).
export interface InlineReplyState {
  threadAddress: string;
  emailId: string;
  mode: ComposeMode;
}

// ── Send flash (log-line confirmation, terminal-style) ───────────────────
// When a send completes, a transient "now → you · sent ..." line appears
// at the top of the thread log and auto-fades after ~4s. The real email
// will arrive shortly via the Firestore listener and re-render in place.
export interface SendFlash {
  id: string;          // unique id for keying + clearing
  threadAddress: string;
  subject: string;
  at: Date;
}

/**
 * TerminalWorkspaceStream — the right pane that shows whatever's currently
 * selected in the rail. One of:
 *   - thread      → bespoke email log (commit-1 ships read-only)
 *   - task        → existing TaskDetailView (wholesale)
 *   - invoice     → terse invoice summary + "open in modal" affordance
 *   - nothing     → an overview / welcome screen
 *
 * (The legacy in-modal LLM chat pane was retired 2026-06 — job chat now lives
 *  in the app-wide Andrei panel, opened via the [andrei] status-bar button.)
 */

// relTime is imported from the shared terminal formatter —
// '../../../themes/terminal/relTime'. Single source of truth for every
// timestamp rendered in the terminal UI so formats never drift.

// ── Quoted-tail stripper for email snippets ──────────────────────────────
function snippetFor(email: FeedEmailData): string {
  const msg  = (email.message || email.text || '').trim();
  const cleaned = msg.split(/\n/).filter(l => !/^(>|On\s.+wrote:)/i.test(l.trim())).join(' ');
  if (cleaned.length > 4) return cleaned;
  return email.subject || '(no subject)';
}

// ── Inline HTML sanitizer for email bodies ───────────────────────────────
// Emails carry arbitrary HTML we can't trust. Rather than render in a
// sandboxed iframe (heavy per-message + no inline flow), we parse with
// DOMParser, strip script/iframe/form/style/etc, remove event handlers
// and javascript: URLs, and force external links to open safely in new
// tabs. Images stay in — the terminal SCSS dims them so they don't
// blow out the dark canvas.
function sanitizeEmailHtml(html: string): string {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Strip dangerous / noisy elements entirely. <style> in particular
    // would leak its rules globally when injected via innerHTML.
    const BAN = ['script', 'iframe', 'object', 'embed', 'form', 'input',
                 'button', 'textarea', 'select', 'style', 'link', 'meta',
                 'base', 'title'];
    BAN.forEach(tag => doc.querySelectorAll(tag).forEach(el => el.remove()));

    // Remove every on*-handler and any javascript:/data: URL attribute.
    doc.querySelectorAll('*').forEach(el => {
      for (const attr of Array.from(el.attributes)) {
        const n = attr.name.toLowerCase();
        const v = String(attr.value || '').toLowerCase().trim();
        if (n.startsWith('on')) el.removeAttribute(attr.name);
        if ((n === 'href' || n === 'src' || n === 'action') &&
            (v.startsWith('javascript:') || v.startsWith('vbscript:'))) {
          el.removeAttribute(attr.name);
        }
      }
    });

    // External links: open in a new tab, strip referrer.
    doc.querySelectorAll('a[href]').forEach(a => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer nofollow');
    });

    // Images: keep but force lazy-load so long threads don't stall; strip
    // any inline width/height that blows past our column width.
    doc.querySelectorAll('img').forEach(img => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('referrerpolicy', 'no-referrer');
      img.removeAttribute('width');
      img.removeAttribute('height');
    });

    return doc.body ? doc.body.innerHTML : '';
  } catch {
    return '';
  }
}

// ─────────────────────────────────────────────────────────────────────────

export interface TerminalWorkspaceStreamProps {
  selection: RailSelection | null;
  // Data sources for the current selection
  emails: FeedEmailData[];
  tasks: Task[];
  invoices: Invoice[];

  // Customer/job context (for empty state)
  customerInfo: CustomerInfo | null;
  jobLabel: string;
  /** Job id — stamped onto outbound emails sent from the composer. */
  linkedJobId: string;

  // Task callbacks
  onTaskUpdated: (t: Task) => void;
  onTaskDeleted: (id: string) => void;
  onTaskClose: () => void;

  // Email click — opens the EmailPanelContext detail view (used from [open full])
  onEmailOpen: (email: FeedEmailData) => void;

  // ── Commit 2a wiring ───────────────────────────────────────────────
  /** Current inline-reply state owned by the modal (nullable). */
  inlineReply: InlineReplyState | null;
  /** Start an inline reply/replyAll/forward under a specific email. */
  onStartInlineReply: (next: InlineReplyState) => void;
  /** Close the inline composer without discarding (draft auto-save holds content). */
  onCloseInlineReply: () => void;
  /** Promote the inline composer into its own pane slot. The optional
   *  draftId is the id of the in-flight draft that was just auto-saved
   *  — the caller uses it to hydrate the new pane from that draft so
   *  the user's typed body survives the transition. */
  onPromoteInlineToPane: (state: InlineReplyState, draftId: string | null) => void;
  /** Discard the inline composer — drops the composer and deletes the Firestore draft. */
  onDiscardInlineReply: () => void;
  /** Send-success flashes currently shown in this thread (auto-cleared after ~4s). */
  sendFlashes: SendFlash[];
  /** Called from any composer when send completes successfully — modal records a flash. */
  onComposerSent: (ctx: { threadAddress: string; subject: string; mode: ComposeMode }) => void;
  /** Close a compose pane (pane variant). */
  onCloseComposePane: () => void;
  /** Discard a compose pane + its draft (pane variant). */
  onDiscardComposePane: () => void;

  // ── Commit 2b wiring ───────────────────────────────────────────────
  /** All job-scoped drafts (filtered to this customer by the modal). */
  jobDrafts: EmailDraft[];
  /** Start a fresh compose (thread pane calls this with the thread's address). */
  onStartNewEmail: (threadAddress?: string) => void;
  /** Resume an existing draft: opens a compose pane, sets draftMode='edit'. */
  onResumeDraft: (draft: EmailDraft) => void;
  /** Permanently delete a draft. */
  onDeleteDraft: (draft: EmailDraft) => void;

  /** Ranked list the host wants prioritized in the recipient autocomplete
   *  (job's customer + their sub-contacts). Passed through to composer. */
  priorityEmails?: EmailOption[];
}

const TerminalWorkspaceStream: React.FC<TerminalWorkspaceStreamProps> = ({
  selection,
  emails,
  tasks,
  invoices,
  customerInfo,
  jobLabel,
  linkedJobId,
  onTaskUpdated,
  onTaskDeleted,
  onTaskClose,
  onEmailOpen,
  inlineReply,
  onStartInlineReply,
  onCloseInlineReply,
  onPromoteInlineToPane,
  onDiscardInlineReply,
  sendFlashes,
  onComposerSent,
  onCloseComposePane,
  onDiscardComposePane,
  jobDrafts,
  onStartNewEmail,
  onResumeDraft,
  onDeleteDraft,
  priorityEmails,
}) => {
  // Compute thread count up-front so the overview pane can show it without
  // gating a hook behind a conditional return.
  const threadCount = useMemo(() => groupEmailsByThread(emails).length, [emails]);

  // No selection → overview
  if (!selection) {
    return (
      <OverviewPane
        jobLabel={jobLabel}
        customerInfo={customerInfo}
        threadCount={threadCount}
        taskCount={tasks.length}
        invoiceCount={invoices.length}
      />
    );
  }

  // Email thread
  if (selection.kind === 'thread') {
    return (
      <EmailThreadPane
        address={selection.id}
        emails={emails}
        linkedJobId={linkedJobId}
        inlineReply={inlineReply && inlineReply.threadAddress === selection.id ? inlineReply : null}
        onStartInlineReply={onStartInlineReply}
        onCloseInlineReply={onCloseInlineReply}
        onPromoteInlineToPane={onPromoteInlineToPane}
        onDiscardInlineReply={onDiscardInlineReply}
        sendFlashes={sendFlashes.filter(f => f.threadAddress === selection.id)}
        onComposerSent={onComposerSent}
        onEmailOpen={onEmailOpen}
        drafts={jobDrafts}
        onStartNewEmail={onStartNewEmail}
        onResumeDraft={onResumeDraft}
        onDeleteDraft={onDeleteDraft}
        priorityEmails={priorityEmails}
      />
    );
  }

  // Compose (pane variant)
  if (selection.kind === 'compose' && selection.compose) {
    // Locate the source email by id (for reply/replyAll/forward). Search both
    // the job emails and — just in case the email isn't in the current feed
    // yet — fall back to null; TerminalComposer handles that safely.
    const sourceEmail = selection.compose.replyToEmailId
      ? emails.find(e => e.id === selection.compose!.replyToEmailId) || null
      : null;
    const threadAddr = selection.compose.threadAddress || '';
    return (
      <div className="term-stream term-stream--compose">
        <TerminalComposer
          variant="pane"
          mode={selection.compose.mode}
          sourceEmail={sourceEmail}
          defaultTo={
            // For 'new' mode, prefill with the customer's primary email if
            // the thread context isn't set.
            selection.compose.mode === 'new'
              ? (customerInfo?.email || threadAddr || '')
              : undefined
          }
          resumeDraftId={selection.compose.draftId}
          linkedJobId={linkedJobId}
          priorityEmails={priorityEmails}
          onSendSuccess={({ subject, mode }) => {
            onComposerSent({ threadAddress: threadAddr, subject, mode });
            onCloseComposePane();
          }}
          onDiscard={onDiscardComposePane}
          onClose={onCloseComposePane}
        />
      </div>
    );
  }

  // Task
  if (selection.kind === 'task') {
    const task = tasks.find(t => t.id === selection.id);
    if (!task) {
      return <div className="term-stream__empty">task no longer available</div>;
    }
    return (
      <div className="term-stream term-stream--task">
        <TaskDetailView
          task={task}
          onClose={onTaskClose}
          heightPercent={100}
          onTaskUpdated={onTaskUpdated}
          onTaskDeleted={onTaskDeleted}
        />
      </div>
    );
  }

  // Invoices no longer mount as a pane — the rail routes them directly
  // into InvoiceViewModal. Keeping the `kind: 'invoice'` branch as a
  // defensive fallback in case stale selections linger in memory.
  if (selection.kind === 'invoice') {
    return <div className="term-stream__empty">(invoices open in the modal)</div>;
  }

  return <div className="term-stream__empty">unknown selection</div>;
};

// ─────────────────────────────────────────────────────────────────────────
// Overview — what you see when nothing is selected.
// Keep it terse. Some quick stats and prompts to pick something.
// ─────────────────────────────────────────────────────────────────────────

interface OverviewProps {
  jobLabel: string;
  customerInfo: CustomerInfo | null;
  threadCount: number;
  taskCount: number;
  invoiceCount: number;
}
const OverviewPane: React.FC<OverviewProps> = ({
  jobLabel, customerInfo, threadCount, taskCount, invoiceCount
}) => (
  <div className="term-stream term-stream--overview">
    <div className="term-stream__overview-block">
      <div className="term-stream__overview-prompt">$ summary {jobLabel}</div>
      <div className="term-stream__overview-line">
        <span className="term-stream__overview-key">customer:</span>
        <span className="term-stream__overview-val">
          {customerInfo?.name || customerInfo?.company || '(unknown)'}
        </span>
      </div>
      <div className="term-stream__overview-line">
        <span className="term-stream__overview-key">threads:</span>
        <span className="term-stream__overview-val">{threadCount}</span>
      </div>
      <div className="term-stream__overview-line">
        <span className="term-stream__overview-key">tasks:</span>
        <span className="term-stream__overview-val">{taskCount}</span>
      </div>
      <div className="term-stream__overview-line">
        <span className="term-stream__overview-key">invoices:</span>
        <span className="term-stream__overview-val">{invoiceCount}</span>
      </div>
      <div className="term-stream__overview-hint">
        ── pick anything from the rail to view it here ──
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Email thread pane — shows the full chronological log for one correspondent.
// Click an email row to expand its body inline. (Commit-1 read-only; reply
// composer comes in commit 2.)
// ─────────────────────────────────────────────────────────────────────────

interface ThreadPaneProps {
  address: string;
  emails: FeedEmailData[];
  linkedJobId: string;
  inlineReply: InlineReplyState | null;
  onStartInlineReply: (next: InlineReplyState) => void;
  onCloseInlineReply: () => void;
  onPromoteInlineToPane: (state: InlineReplyState, draftId: string | null) => void;
  onDiscardInlineReply: () => void;
  sendFlashes: SendFlash[];
  onComposerSent: (ctx: { threadAddress: string; subject: string; mode: ComposeMode }) => void;
  onEmailOpen: (email: FeedEmailData) => void;
  drafts: EmailDraft[];
  onStartNewEmail: (threadAddress?: string) => void;
  onResumeDraft: (draft: EmailDraft) => void;
  onDeleteDraft: (draft: EmailDraft) => void;
  priorityEmails?: EmailOption[];
}

// Filter drafts to the ones belonging to this thread. A draft belongs when
// any recipient (to/cc/bcc) matches the thread's address, OR when the draft
// is a reply/forward whose source email is in this thread.
function draftsForThread(drafts: EmailDraft[], thread: { address: string; emails: FeedEmailData[] }): EmailDraft[] {
  const addr = thread.address.toLowerCase();
  const threadEmailIds = new Set(thread.emails.map(e => e.id));
  return drafts.filter(d => {
    if (d.status && d.status !== 'draft') return false;
    const recip = [
      ...(d.to || []),
      ...(d.cc || []),
      ...(d.bcc || []),
    ].map(a => String(a || '').toLowerCase().trim());
    if (recip.includes(addr)) return true;
    if (d.replyToEmailId && threadEmailIds.has(d.replyToEmailId)) return true;
    return false;
  });
}

const EmailThreadPane: React.FC<ThreadPaneProps> = ({
  address, emails, linkedJobId,
  inlineReply, onStartInlineReply, onCloseInlineReply, onPromoteInlineToPane, onDiscardInlineReply,
  sendFlashes, onComposerSent,
  onEmailOpen,
  drafts, onStartNewEmail, onResumeDraft, onDeleteDraft,
  priorityEmails,
}) => {
  const threads = useMemo(() => groupEmailsByThread(emails), [emails]);
  const thread = threads.find(t => t.address === address);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Auto-expand the email currently being replied to so the user doesn't
  // have to hunt for it. If they collapse it manually the composer still
  // renders — we scope expansion to the row head, not the composer.
  useEffect(() => {
    if (inlineReply && inlineReply.threadAddress === address) {
      setExpanded(inlineReply.emailId);
    }
  }, [inlineReply, address]);

  // Thread-scoped drafts go at the bottom of the log as their own entry type.
  // Must live ABOVE the early-return below (rules of hooks).
  const threadDrafts = useMemo(
    () => thread ? draftsForThread(drafts, thread) : [],
    [drafts, thread]
  );

  // ── Lazy full-body hydration ──────────────────────────────────────
  // Graph emails arrive with just a preview (email.html/text is truncated
  // at ~200-300 chars). When the user expands an entry we fetch the full
  // body via getOrFetchGraphEmailBody, cache it by id, and re-render with
  // the full HTML. IMAP emails already have their full body and bypass
  // this path.
  const [hydratedBody, setHydratedBody] =
    useState<Record<string, { html: string | null; text: string }>>({});
  const hydratingRef = useRef<Set<string>>(new Set());

  const hydrate = useCallback(async (email: FeedEmailData) => {
    // Already cached in our local state?
    if (hydratedBody[email.id]) return;
    if (hydratingRef.current.has(email.id)) return;

    const currentHtml = email.html ?? null;
    const currentText = email.text ?? '';
    const hasFullHtml = !!(currentHtml && currentHtml.length > 200);
    const hasFullText = !!(currentText && currentText.length > 300);

    const isGraph = email.source === 'graph';
    const graphId = email.graph?.id;
    const emailId = (email as any).emailId || email.id;

    // Resolve cid: inline images for Graph HTML. For non-Graph emails we
    // can't fetch the attachments, so just strip the cid: URLs (otherwise
    // the browser fires ERR_UNKNOWN_URL_SCHEME for every inline image).
    const resolveImages = async (html: string | null): Promise<string | null> => {
      if (!html) return html;
      if (!/cid:/i.test(html)) return html;
      if (isGraph && emailId) {
        try {
          return await resolveInlineGraphImages(emailId, html);
        } catch (err) {
          console.warn('[TerminalWorkspaceStream] Failed to resolve inline images:', err);
          return stripCidImages(html);
        }
      }
      return stripCidImages(html);
    };

    if (hasFullHtml || hasFullText) {
      const resolved = await resolveImages(currentHtml);
      setHydratedBody(prev => ({ ...prev, [email.id]: { html: resolved, text: currentText } }));
      return;
    }

    if (!isGraph || !graphId || !emailId) {
      // IMAP or unknown source — nothing more to fetch. Still try the
      // resolver so any cid: refs in the preview are cleaned up.
      const resolved = await resolveImages(currentHtml);
      setHydratedBody(prev => ({ ...prev, [email.id]: { html: resolved, text: currentText } }));
      return;
    }

    // Check the shared cache first (populated by EmailView).
    const cachedHtml = getCachedGraphEmailBody(emailId, 'html');
    if (cachedHtml?.content) {
      const resolved = await resolveImages(cachedHtml.content);
      setHydratedBody(prev => ({ ...prev, [email.id]: { html: resolved, text: currentText } }));
      return;
    }
    const cachedText = getCachedGraphEmailBody(emailId, 'text');
    if (cachedText?.content) {
      setHydratedBody(prev => ({ ...prev, [email.id]: { html: currentHtml, text: cachedText.content } }));
      return;
    }

    // Slow path: fetch from Graph. Track in-flight requests so rapid
    // click-expand-collapse-expand doesn't fire duplicate fetches.
    hydratingRef.current.add(email.id);
    try {
      const body = await getOrFetchGraphEmailBody(emailId, 'html');
      if (body?.content) {
        if (body.contentType === 'html') {
          const resolved = await resolveImages(body.content);
          setHydratedBody(prev => ({ ...prev, [email.id]: { html: resolved, text: currentText } }));
        } else {
          setHydratedBody(prev => ({ ...prev, [email.id]: { html: currentHtml, text: body.content } }));
        }
      } else {
        setHydratedBody(prev => ({ ...prev, [email.id]: { html: currentHtml, text: currentText } }));
      }
    } catch (err) {
      console.warn('[TerminalWorkspaceStream] Failed to hydrate email body:', err);
      setHydratedBody(prev => ({ ...prev, [email.id]: { html: currentHtml, text: currentText } }));
    } finally {
      hydratingRef.current.delete(email.id);
    }
  }, [hydratedBody]);

  // Whenever an email is expanded (either by click or auto-expand for
  // inline reply), kick off hydration if not already cached.
  useEffect(() => {
    if (!expanded) return;
    const email = thread?.emails.find(e => e.id === expanded);
    if (email) hydrate(email);
  }, [expanded, thread, hydrate]);

  if (!thread) {
    return <div className="term-stream__empty">no messages with this contact yet</div>;
  }

  return (
    <div className="term-stream term-stream--thread">
      <div className="term-stream__thread-header">
        <span className="term-stream__thread-with">thread with</span>
        <span className="term-stream__thread-name">{thread.displayName}</span>
        <span className="term-stream__thread-addr">&lt;{thread.address}&gt;</span>
        <span className="term-stream__thread-count">· {thread.emails.length} message{thread.emails.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Action row — its own line so it doesn't crowd the pane [×] close
          button or the thread header. Reads as a subtle prompt under the
          thread title. */}
      <div className="term-stream__thread-actions">
        <button
          type="button"
          className="term-stream__thread-new"
          onClick={() => onStartNewEmail(address)}
          title="Start a new email to this correspondent (not a reply)"
        >
          <span className="term-stream__action-bracket">[</span>+ new in thread<span className="term-stream__action-bracket">]</span>
        </button>
      </div>

      {/* Newest at top so the active thread reads down chronologically as
          you scroll back. Matches how a tail/log feels. */}
      <div className="term-stream__thread-log">
        {/* ── Send-success flashes (ephemeral log lines) ────────────── */}
        {sendFlashes.map((flash) => (
          <div key={flash.id} className="term-stream__thread-flash" role="status">
            <span className="term-stream__thread-flash-time">now</span>
            <span className="term-stream__thread-flash-arrow" aria-hidden="true">→</span>
            <span className="term-stream__thread-flash-who">you</span>
            <span className="term-stream__thread-flash-msg">
              sent <em>"{flash.subject}"</em>
            </span>
          </div>
        ))}

        {thread.emails.map((email) => {
          const isOut = email.folder === 'sent' || email.emailType === 'sent';
          const isOpen = expanded === email.id;
          const showInlineComposer = !!inlineReply && inlineReply.emailId === email.id;
          return (
            <div
              key={email.id}
              className={
                'term-stream__thread-entry'
                + (isOut ? ' term-stream__thread-entry--out' : ' term-stream__thread-entry--in')
                + (isOpen ? ' term-stream__thread-entry--open' : '')
              }
            >
              <button
                type="button"
                className="term-stream__thread-entry-head"
                onClick={() => {
                  const next = isOpen ? null : email.id;
                  setExpanded(next);
                  // When the user opens an unread email, flip it to
                  // read in Firestore (and propagate to Graph for
                  // Outlook-sourced messages). Fire-and-forget — errors
                  // are logged inside the helper. auto: expanding a message is
                  // not a deliberate "mark read" click, so an owner-only
                  // refusal (the second user) stays quiet instead of toasting
                  // on every expand.
                  if (next && !isEmailRead(email)) {
                    markEmailRead(email, { auto: true });
                  }
                }}
                title={isOpen ? 'Collapse' : 'Expand'}
              >
                <span className="term-stream__thread-entry-time">{relTime(email.createdAt)}</span>
                <span className="term-stream__thread-entry-arrow" aria-hidden="true">
                  {isOut ? '→' : '←'}
                </span>
                <span className="term-stream__thread-entry-who">
                  {isOut ? 'you' : (email.from?.[0]?.name || email.from?.[0]?.address || 'unknown')}
                </span>
                <span className="term-stream__thread-entry-subj">
                  {email.subject || '(no subject)'}
                </span>
              </button>
              {isOpen ? (
                <div className="term-stream__thread-entry-body">
                  {(() => {
                    // Prefer the hydrated copy (lazy-fetched from Graph)
                    // over the preview that lives on the email doc. If
                    // still fetching, show a dim loading line.
                    const full = hydratedBody[email.id];
                    const html = (full?.html ?? email.html) || '';
                    const text = full?.text ?? email.text ?? '';
                    const loading = !full && hydratingRef.current.has(email.id);
                    if (loading) {
                      return (
                        <div className="term-stream__thread-entry-loading">
                          loading full message…
                        </div>
                      );
                    }
                    if (html) {
                      return (
                        <div
                          className="term-stream__thread-entry-html"
                          dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(html) }}
                        />
                      );
                    }
                    return (
                      <pre className="term-stream__thread-entry-snippet">
                        {text || snippetFor(email)}
                      </pre>
                    );
                  })()}
                  <div className="term-stream__thread-entry-actions">
                    <ReplyMenu
                      onReply={() => onStartInlineReply({ threadAddress: address, emailId: email.id, mode: 'reply' })}
                      onReplyAll={() => onStartInlineReply({ threadAddress: address, emailId: email.id, mode: 'replyAll' })}
                      onForward={() => onStartInlineReply({ threadAddress: address, emailId: email.id, mode: 'forward' })}
                      disabled={showInlineComposer}
                    />
                    {/* [open full] action removed — the expanded view above
                        now shows the full hydrated HTML body. The classic
                        side-panel slide-in renders underneath the fullscreen
                        workspace modal so opening it from here was a dead
                        link. If a user needs the iframe-rendered email view
                        for max fidelity, they can switch themes. */}
                  </div>

                  {/* ── Inline composer mounts directly below the message ─── */}
                  {showInlineComposer && inlineReply && (
                    <div className="term-stream__thread-inline-compose">
                      <TerminalComposer
                        variant="inline"
                        mode={inlineReply.mode}
                        sourceEmail={email}
                        linkedJobId={linkedJobId}
                        priorityEmails={priorityEmails}
                        onSendSuccess={({ subject, mode }) => {
                          onComposerSent({ threadAddress: address, subject, mode });
                          onCloseInlineReply();
                        }}
                        onClose={onCloseInlineReply}
                        onDiscard={onDiscardInlineReply}
                        onExpandToPane={({ draftId }) => onPromoteInlineToPane(inlineReply, draftId)}
                      />
                    </div>
                  )}
                </div>
              ) : (
                // Preview now wraps up to 2 lines — give it enough text to
                // actually fill both lines on a wide pane without going
                // wasteful on narrow panes (line-clamp handles overflow).
                <div className="term-stream__thread-entry-preview">
                  {snippetFor(email).slice(0, 320)}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Saved drafts for this thread (bottom of log) ─────────────
            These surface here instead of in a separate "drafts" section
            so you always see "what's left unfinished" alongside the
            messages that prompted it. [resume] reopens in a compose pane
            with the saved content hydrated; [discard] deletes permanently. */}
        {threadDrafts.map((draft) => {
          // Firestore Timestamp has .toDate(); fall back to epoch if missing.
          const ts: any = draft.lastSavedAt;
          const savedAt: Date =
            ts?.toDate ? ts.toDate()
            : ts?.seconds ? new Date(ts.seconds * 1000)
            : (ts instanceof Date ? ts : new Date());
          const recipient = (draft.to && draft.to[0]) || address;
          const draftKind = draft.isReply ? 'reply' : (draft.isForward ? 'forward' : 'new');
          return (
            <div
              key={`draft-${draft.id}`}
              className="term-stream__thread-draft"
              role="group"
              aria-label={`Saved ${draftKind} draft to ${recipient}`}
            >
              <span className="term-stream__thread-draft-glyph" aria-hidden="true">▸</span>
              <span className="term-stream__thread-draft-label">
                draft ({draftKind})
              </span>
              <span className="term-stream__thread-draft-meta">
                saved {relTime(savedAt)} · to {recipient}
              </span>
              <span className="term-stream__thread-draft-actions">
                <button
                  type="button"
                  className="term-stream__thread-entry-action"
                  onClick={(e) => { e.stopPropagation(); onResumeDraft(draft); }}
                  title="Open this draft in a compose pane"
                >
                  <span className="term-stream__action-bracket">[</span>resume<span className="term-stream__action-bracket">]</span>
                </button>
                <button
                  type="button"
                  className="term-stream__thread-entry-action term-stream__thread-entry-action--danger"
                  onClick={(e) => { e.stopPropagation(); onDeleteDraft(draft); }}
                  title="Permanently delete this draft"
                >
                  <span className="term-stream__action-bracket">[</span>discard<span className="term-stream__action-bracket">]</span>
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Reply mini-menu ──────────────────────────────────────────────────────
// Single [reply ▾] trigger. Opens a small text list on click with reply,
// reply-all, forward items. Closes on outside click or item selection.
interface ReplyMenuProps {
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  /** When true, grey out the trigger (composer already open for this email). */
  disabled: boolean;
}
const ReplyMenu: React.FC<ReplyMenuProps> = ({ onReply, onReplyAll, onForward, disabled }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (ev: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(ev.target as Node)) setOpen(false);
    };
    const t = window.setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', h); };
  }, [open]);

  const pick = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    fn();
  };

  return (
    <span className="term-reply-menu" ref={rootRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="term-stream__thread-entry-action"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(o => !o); }}
        title={disabled ? 'Composer is already open for this email' : 'Reply options'}
      >
        <span className="term-stream__action-bracket">[</span>reply <span className="term-reply-menu__caret">▾</span><span className="term-stream__action-bracket">]</span>
      </button>
      {open && !disabled && (
        <div className="term-reply-menu__items" role="menu">
          <button type="button" className="term-reply-menu__item" onClick={pick(onReply)} role="menuitem">
            <span className="term-reply-menu__glyph">&gt;</span> reply
          </button>
          <button type="button" className="term-reply-menu__item" onClick={pick(onReplyAll)} role="menuitem">
            <span className="term-reply-menu__glyph">&gt;</span> reply-all
          </button>
          <button type="button" className="term-reply-menu__item" onClick={pick(onForward)} role="menuitem">
            <span className="term-reply-menu__glyph">&gt;</span> forward
          </button>
        </div>
      )}
    </span>
  );
};

export default TerminalWorkspaceStream;
